import { authAPIClient } from '../lib/authAPI';

/**
 * Task Service - Frontend service layer for E8 Tasks management
 * Handles all task-related operations through Supabase Edge Functions
 */
class TaskService {
  constructor() {
    this.cache = new Map();
    this.cacheTTL = 5 * 60 * 1000; // 5 minutes cache TTL
    this.organisationId = null;
    this.engagementId = null;
  }

  setOrganisation(organisationId, engagementId = null) {
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(organisationId || '')) {
      throw new Error('A valid active organisation is required');
    }
    if (this.organisationId !== organisationId || this.engagementId !== engagementId) this.clearCache();
    this.organisationId = organisationId;
    this.engagementId = engagementId;
  }

  clearOrganisation() {
    this.organisationId = null;
    this.engagementId = null;
    this.clearCache();
  }

  requireOrganisation() {
    if (!this.organisationId) throw new Error('Active organisation is required');
    return this.organisationId;
  }

  requireEngagement() {
    if (!this.engagementId) throw new Error('Active engagement is required');
    return this.engagementId;
  }

  /**
   * Get cache key for task queries
   */
  getCacheKey(filters = {}) {
    return `tasks_${JSON.stringify(filters)}`;
  }

  /**
   * Check if cached data is still valid
   */
  isCacheValid(cacheEntry) {
    if (!cacheEntry) return false;
    return Date.now() - cacheEntry.timestamp < this.cacheTTL;
  }

  /**
   * Get tasks with filtering, sorting, and pagination
   * @param {Object} filters - Filter options
   * @param {string} filters.assignee_id - Filter by assignee
   * @param {string} filters.status - Filter by status
   * @param {string} filters.priority - Filter by priority
   * @param {string} filters.control_id - Filter by control ID
   * @param {string} filters.assessment_id - Filter by assessment ID
   * @param {string} filters.search - Search in title and description
   * @param {number} filters.limit - Number of tasks to return (default: 50)
   * @param {number} filters.offset - Offset for pagination (default: 0)
   * @param {string} filters.sort_by - Field to sort by (default: 'created_at')
   * @param {string} filters.sort_order - Sort order 'asc' or 'desc' (default: 'desc')
   * @returns {Object} Tasks response with pagination info
   */
  async getTasks(filters = {}) {
    const organisationId = this.requireOrganisation();
    const engagementId = this.requireEngagement();
    filters = { ...filters, organisation_id: organisationId, engagement_id: engagementId };
    const cacheKey = this.getCacheKey(filters);
    const cached = this.cache.get(cacheKey);
    
    // Return cached data if valid
    if (this.isCacheValid(cached)) {
      console.log('🚀 TaskService: Returning cached tasks');
      return cached.data;
    }

    try {
      console.log('🚀 TaskService: Fetching tasks with filters:', filters);
      
      // Build query parameters
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const response = await authAPIClient.makeRequest(`/tasks-list?${params.toString()}`, {
        method: 'GET'
      });

      // Cache the result
      this.cache.set(cacheKey, {
        data: response,
        timestamp: Date.now()
      });

      console.log(`🚀 TaskService: Successfully fetched ${response.tasks?.length || 0} tasks`);
      return response;
    } catch (error) {
      console.error('🚀 TaskService: Failed to fetch tasks:', error);
      throw new Error(`Failed to fetch tasks: ${error.message}`);
    }
  }

  /**
   * Get a single task by ID
   * @param {string} taskId - Task ID
   * @returns {Object} Task object
   */
  async getTask(taskId) {
    this.requireOrganisation();
    this.requireEngagement();
    try {
      console.log('🚀 TaskService: Fetching task:', taskId);
      
      // Try to get from cache first by checking if it exists in any cached task list
      for (const [key, cacheEntry] of this.cache.entries()) {
        if (this.isCacheValid(cacheEntry) && cacheEntry.data.tasks) {
          const task = cacheEntry.data.tasks.find(t => t.id === taskId);
          if (task) {
            console.log('🚀 TaskService: Returning cached task');
            return { task };
          }
        }
      }

      // If not in cache, fetch single task (we could create a tasks-get endpoint)
      // For now, fetch all tasks and filter
      const response = await this.getTasks({ limit: 1000 });
      const task = response.tasks.find(t => t.id === taskId);
      
      if (!task) {
        throw new Error('Task not found');
      }

      return { task };
    } catch (error) {
      console.error('🚀 TaskService: Failed to fetch task:', error);
      throw new Error(`Failed to fetch task: ${error.message}`);
    }
  }

  /**
   * Create a new task
   * @param {Object} taskData - Task data
   * @param {string} taskData.title - Task title (required)
   * @param {string} taskData.description - Task description
   * @param {string} taskData.control_id - E8 control ID (required)
   * @param {string} taskData.question_id - Specific question ID within control
   * @param {string} taskData.assignee_id - User ID to assign task to
   * @param {string} taskData.priority - Priority level (critical, high, medium, low)
   * @param {string} taskData.task_type - Type of task (remediation, assessment, etc.)
   * @param {string} taskData.due_date - Due date in ISO format
   * @param {number} taskData.estimated_hours - Estimated hours to complete
   * @param {Array} taskData.tags - Array of tags
   * @param {Object} taskData.metadata - Additional metadata
   * @param {string} taskData.assessment_id - Associated assessment ID
   * @returns {Object} Created task object
   */
  async createTask(taskData) {
    const organisationId = this.requireOrganisation();
    if (!this.engagementId) throw new Error('Active engagement is required');
    try {
      console.log('🚀 TaskService: Creating task:', taskData.title);
      
      const response = await authAPIClient.callFunction('tasks-create', {
        ...taskData,
        organisation_id: organisationId,
        engagement_id: this.engagementId
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to create task');
      }

      // Clear cache to ensure fresh data on next fetch
      this.clearCache();

      console.log('🚀 TaskService: Successfully created task:', response.data.task.id);
      return response.data;
    } catch (error) {
      console.error('🚀 TaskService: Failed to create task:', error);
      throw new Error(`Failed to create task: ${error.message}`);
    }
  }

  /**
   * Update an existing task
   * @param {string} taskId - Task ID
   * @param {Object} updates - Fields to update
   * @returns {Object} Updated task object
   */
  async updateTask(taskId, updates) {
    const organisationId = this.requireOrganisation();
    const engagementId = this.requireEngagement();
    try {
      console.log('🚀 TaskService: Updating task:', taskId, 'with updates:', Object.keys(updates));
      
      const updateData = {
        id: taskId,
        ...updates,
        organisation_id: organisationId,
        engagement_id: engagementId
      };

      const response = await authAPIClient.makeRequest(`/tasks-update`, {
        method: 'PUT',
        body: updateData
      });

      // Clear cache to ensure fresh data on next fetch
      this.clearCache();

      console.log('🚀 TaskService: Successfully updated task:', taskId);
      return response;
    } catch (error) {
      console.error('🚀 TaskService: Failed to update task:', error);
      throw new Error(`Failed to update task: ${error.message}`);
    }
  }

  /**
   * Delete a task
   * @param {string} taskId - Task ID
   * @returns {Object} Delete confirmation
   */
  async deleteTask(taskId) {
    const organisationId = this.requireOrganisation();
    const engagementId = this.requireEngagement();
    try {
      console.log('🚀 TaskService: Deleting task:', taskId);
      
      const response = await authAPIClient.callFunction('tasks-delete', {
        id: taskId,
        organisation_id: organisationId,
        engagement_id: engagementId
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete task');
      }

      // Clear cache to ensure fresh data on next fetch
      this.clearCache();

      console.log('🚀 TaskService: Successfully deleted task:', taskId);
      return response.data;
    } catch (error) {
      console.error('🚀 TaskService: Failed to delete task:', error);
      throw new Error(`Failed to delete task: ${error.message}`);
    }
  }

  /**
   * Get comments for a task
   * @param {string} taskId - Task ID
   * @param {Object} options - Query options
   * @param {number} options.limit - Number of comments to return
   * @param {number} options.offset - Offset for pagination
   * @returns {Object} Comments response with pagination
   */
  async getTaskComments(taskId, options = {}) {
    const organisationId = this.requireOrganisation();
    const engagementId = this.requireEngagement();
    try {
      console.log('🚀 TaskService: Fetching comments for task:', taskId);
      
      const params = new URLSearchParams({
        task_id: taskId,
        organisation_id: organisationId,
        engagement_id: engagementId,
        limit: options.limit || 50,
        offset: options.offset || 0
      });

      const response = await authAPIClient.makeRequest(`/tasks-comments?${params.toString()}`, {
        method: 'GET'
      });

      console.log(`🚀 TaskService: Successfully fetched ${response.comments?.length || 0} comments`);
      return response;
    } catch (error) {
      console.error('🚀 TaskService: Failed to fetch comments:', error);
      throw new Error(`Failed to fetch comments: ${error.message}`);
    }
  }

  /**
   * Add a comment to a task
   * @param {string} taskId - Task ID
   * @param {string} commentText - Comment text
   * @param {string} parentCommentId - Parent comment ID for replies (optional)
   * @returns {Object} Created comment object
   */
  async addTaskComment(taskId, commentText, parentCommentId = null) {
    const organisationId = this.requireOrganisation();
    const engagementId = this.requireEngagement();
    try {
      console.log('🚀 TaskService: Adding comment to task:', taskId);
      
      const commentData = {
        task_id: taskId,
        comment_text: commentText,
        parent_comment_id: parentCommentId,
        organisation_id: organisationId,
        engagement_id: engagementId
      };

      const response = await authAPIClient.callFunction('tasks-comments', commentData);

      if (!response.success) {
        throw new Error(response.error || 'Failed to add comment');
      }

      console.log('🚀 TaskService: Successfully added comment');
      return response.data;
    } catch (error) {
      console.error('🚀 TaskService: Failed to add comment:', error);
      throw new Error(`Failed to add comment: ${error.message}`);
    }
  }

  /**
   * Delete a comment from a task
   * @param {string} commentId - Comment ID to delete
   * @returns {Object} Delete confirmation
   */
  async deleteTaskComment(commentId) {
    const organisationId = this.requireOrganisation();
    const engagementId = this.requireEngagement();
    try {
      console.log('🚀 TaskService: Deleting comment:', commentId);
      
      const response = await authAPIClient.makeRequest('/tasks-comments', {
        method: 'DELETE',
        body: {
          comment_id: commentId,
          organisation_id: organisationId,
          engagement_id: engagementId
        }
      });

      console.log('🚀 TaskService: Successfully deleted comment');
      return response;
    } catch (error) {
      console.error('🚀 TaskService: Failed to delete comment:', error);
      throw new Error(`Failed to delete comment: ${error.message}`);
    }
  }

  /**
   * Get tasks for a specific control
   * @param {string} controlId - E8 control ID (e.g., 'ML1-PA-01')
   * @param {Object} options - Additional filter options
   * @returns {Object} Tasks response filtered by control
   */
  async getTasksForControl(controlId, options = {}) {
    return this.getTasks({
      control_id: controlId,
      ...options
    });
  }

  /**
   * Get tasks assigned to current user
   * @param {string} userId - User ID
   * @param {Object} options - Additional filter options
   * @returns {Object} Tasks response filtered by assignee
   */
  async getMyTasks(userId, options = {}) {
    return this.getTasks({
      assignee_id: userId,
      ...options
    });
  }

  /**
   * Get tasks created by current user
   * @param {string} userId - User ID
   * @param {Object} options - Additional filter options
   * @returns {Object} Tasks response filtered by creator
   */
  async getTasksCreatedByMe(userId, options = {}) {
    return this.getTasks({
      creator_id: userId,
      ...options
    });
  }

  /**
   * Get tasks by status
   * @param {string} status - Task status
   * @param {Object} options - Additional filter options
   * @returns {Object} Tasks response filtered by status
   */
  async getTasksByStatus(status, options = {}) {
    return this.getTasks({
      status: status,
      ...options
    });
  }

  /**
   * Get overdue tasks
   * @param {Object} options - Additional filter options
   * @returns {Object} Tasks response filtered for overdue items
   */
  async getOverdueTasks(options = {}) {
    // This would require backend logic to filter by due_date < now
    // For now, get all tasks and filter client-side
    const allTasks = await this.getTasks({ ...options, limit: 1000 });
    const now = new Date();
    
    const overdueTasks = allTasks.tasks.filter(task => {
      if (!task.due_date) return false;
      const dueDate = new Date(task.due_date);
      return dueDate < now && task.status !== 'completed' && task.status !== 'cancelled';
    });

    return {
      tasks: overdueTasks,
      pagination: {
        ...allTasks.pagination,
        total: overdueTasks.length
      }
    };
  }

  /**
   * Update task status
   * @param {string} taskId - Task ID
   * @param {string} status - New status
   * @returns {Object} Updated task
   */
  async updateTaskStatus(taskId, status) {
    return this.updateTask(taskId, { status });
  }

  /**
   * Assign task to user
   * @param {string} taskId - Task ID
   * @param {string} assigneeId - Current user ID for self-assignment. Peer assignment requires a membership-aware Issue #21 contract.
   * @returns {Object} Updated task
   */
  async assignTask(taskId, assigneeId) {
    return this.updateTask(taskId, { assignee_id: assigneeId });
  }

  /**
   * Set task priority
   * @param {string} taskId - Task ID
   * @param {string} priority - Priority level
   * @returns {Object} Updated task
   */
  async setTaskPriority(taskId, priority) {
    return this.updateTask(taskId, { priority });
  }

  /**
   * Set task due date
   * @param {string} taskId - Task ID
   * @param {string} dueDate - Due date in ISO format
   * @returns {Object} Updated task
   */
  async setTaskDueDate(taskId, dueDate) {
    return this.updateTask(taskId, { due_date: dueDate });
  }

  /**
   * Get task statistics
   * @param {Object} filters - Optional filters
   * @returns {Object} Task statistics
   */
  async getTaskStats(filters = {}) {
    try {
      const tasks = await this.getTasks({ ...filters, limit: 10000 });
      
      const stats = {
        total: tasks.tasks.length,
        by_status: {},
        by_priority: {},
        overdue: 0,
        completed_this_week: 0,
        created_this_week: 0
      };

      const now = new Date();
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      tasks.tasks.forEach(task => {
        // Count by status
        stats.by_status[task.status] = (stats.by_status[task.status] || 0) + 1;
        
        // Count by priority
        stats.by_priority[task.priority] = (stats.by_priority[task.priority] || 0) + 1;
        
        // Count overdue
        if (task.due_date && new Date(task.due_date) < now && 
            task.status !== 'completed' && task.status !== 'cancelled') {
          stats.overdue++;
        }
        
        // Count completed this week
        if (task.completed_at && new Date(task.completed_at) > weekAgo) {
          stats.completed_this_week++;
        }
        
        // Count created this week
        if (new Date(task.created_at) > weekAgo) {
          stats.created_this_week++;
        }
      });

      return stats;
    } catch (error) {
      console.error('🚀 TaskService: Failed to get task stats:', error);
      throw new Error(`Failed to get task stats: ${error.message}`);
    }
  }

  /**
   * Clear all cached data
   */
  clearCache() {
    this.cache.clear();
    console.log('🚀 TaskService: Cache cleared');
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
      ttl: this.cacheTTL
    };
  }

  /**
   * Get attachments for a task
   * @param {string} taskId - Task ID
   * @returns {Object} Attachments response
   */
  async getTaskAttachments(taskId) {
    const organisationId = this.requireOrganisation();
    const engagementId = this.requireEngagement();
    try {
      console.log('🚀 TaskService: Fetching attachments for task:', taskId);
      
      const response = await authAPIClient.makeRequest(`/tasks-attachments?task_id=${taskId}&organisation_id=${organisationId}&engagement_id=${engagementId}`, {
        method: 'GET'
      });

      console.log(`🚀 TaskService: Successfully fetched ${response.attachments?.length || 0} attachments`);
      return response;
    } catch (error) {
      console.error('🚀 TaskService: Failed to fetch attachments:', error);
      throw new Error(`Failed to fetch attachments: ${error.message}`);
    }
  }

  /**
   * Upload attachment to a task
   * @param {string} taskId - Task ID
   * @param {File} file - File to upload
   * @param {Function} onProgress - Progress callback (optional)
   * @returns {Object} Upload response with attachment info
   */
  async uploadTaskAttachment(taskId, file, onProgress = null) {
    const organisationId = this.requireOrganisation();
    const engagementId = this.requireEngagement();
    try {
      console.log('🚀 TaskService: Uploading attachment to task:', taskId, 'file:', file.name);
      
      const formData = new FormData();
      formData.append('task_id', taskId);
      formData.append('organisation_id', organisationId);
      formData.append('engagement_id', engagementId);
      formData.append('file', file);

      const response = await authAPIClient.makeRequest('/tasks-attachments', {
        method: 'POST',
        body: formData,
        headers: {
          // Don't set Content-Type, let browser set it for FormData
        }
      });

      console.log('🚀 TaskService: Successfully uploaded attachment:', response.attachment.id);
      return response;
    } catch (error) {
      console.error('🚀 TaskService: Failed to upload attachment:', error);
      throw new Error(`Failed to upload attachment: ${error.message}`);
    }
  }

  /**
   * Delete a task attachment
   * @param {string} attachmentId - Attachment ID to delete
   * @returns {Object} Delete confirmation
   */
  async deleteTaskAttachment(attachmentId) {
    const organisationId = this.requireOrganisation();
    const engagementId = this.requireEngagement();
    try {
      console.log('🚀 TaskService: Deleting attachment:', attachmentId);
      
      const response = await authAPIClient.makeRequest('/tasks-attachments', {
        method: 'DELETE',
        body: {
          attachment_id: attachmentId,
          organisation_id: organisationId,
          engagement_id: engagementId
        }
      });

      console.log('🚀 TaskService: Successfully deleted attachment');
      return response;
    } catch (error) {
      console.error('🚀 TaskService: Failed to delete attachment:', error);
      throw new Error(`Failed to delete attachment: ${error.message}`);
    }
  }

  /**
   * Download a task attachment
   * @param {string} attachmentId - Attachment ID
   * @returns {Object} Download response
   */
  async downloadTaskAttachment(attachmentId) {
    const organisationId = this.requireOrganisation();
    const engagementId = this.requireEngagement();
    try {
      console.log('🚀 TaskService: Downloading attachment:', attachmentId);
      
      const response = await authAPIClient.makeRequest(`/tasks-attachments?attachment_id=${attachmentId}&organisation_id=${organisationId}&engagement_id=${engagementId}`, {
        method: 'GET'
      });

      console.log('🚀 TaskService: Successfully got download URL');
      return response;
    } catch (error) {
      console.error('🚀 TaskService: Failed to download attachment:', error);
      throw new Error(`Failed to download attachment: ${error.message}`);
    }
  }

  /**
   * Preload tasks for better performance
   * @param {Object} filters - Filters to preload
   */
  async preloadTasks(filters = {}) {
    try {
      await this.getTasks(filters);
      console.log('🚀 TaskService: Preloaded tasks with filters:', filters);
    } catch (error) {
      console.warn('🚀 TaskService: Failed to preload tasks:', error);
    }
  }
}

// Export singleton instance
export const taskService = new TaskService();
export default taskService;
